import { isAPIError } from "lib/api/base";
import { normalizeAnswers, postAnswers } from "utils/GuidedSignUp";
import { GuidedSignUpStepSlug } from "utils/hooks/useGuidedSignUp";
import { FormikSubmitStep } from "components/Step/stepTypes";
import { FormikHelpers } from "formik";
import { Subscription } from "lib/api/subscription";
import { BudgetValues } from "components/Step/stepTypes";
import { ParsedUrlQuery } from "querystring";

export type GuidedSignUpStepSlugValue =
  typeof GuidedSignUpStepSlug[keyof typeof GuidedSignUpStepSlug];

export function getContentfulQuizStep(
  contentfulQuizModel: any,
  slug: GuidedSignUpStepSlugValue
) {
  return contentfulQuizModel?.quizSteps?.find(
    (step: { slug: GuidedSignUpStepSlugValue }) => step.slug === slug
  );
}

export async function gsuSubmitAnswersDefault<Values>({
  contentfulQuizModel,
  formikHelpers,
  onError,
  onSubmit,
  step,
  subscription,
  values,
}: {
  contentfulQuizModel: any;
  formikHelpers?: FormikHelpers<Values>;
  onError?: (res: any) => void;
  onSubmit: FormikSubmitStep<Values>;
  step: any;
  subscription?: Subscription;
  values: Values;
}) {
  const normalizedAnswers = normalizeAnswers({ answers: values, step });

  const res = await postAnswers({
    normalizedAnswers,
    quizSlug: contentfulQuizModel.slug,
    quizVersion: contentfulQuizModel.version,
    step,
    subscription,
  });

  if (isAPIError(res)) {
    onError?.(res);
  } else {
    onSubmit?.(values, formikHelpers);
  }
}

export function isBudgetRangeGreaterThan(
  budgetValues: Partial<BudgetValues> | null | undefined,
  comparisonNumber: number
): boolean {
  const budgetRangeRaw = budgetValues?.["reader-budget"];
  const budgetRangeMatch =
    typeof budgetRangeRaw === "string"
      ? budgetRangeRaw.match(
          /^\s*\[\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\]\s*$/
        )
      : null;

  return Boolean(
    budgetRangeMatch && Number(budgetRangeMatch[2]) > comparisonNumber
  );
}

export const FALL25_COUPON_CODE = "495fall25";

export function isCouponClaimedByQuery(
  query: ParsedUrlQuery,
  couponCode = FALL25_COUPON_CODE
): boolean {
  const extCouponCode = query.extCouponCode;
  if (Array.isArray(extCouponCode)) {
    return extCouponCode.includes(couponCode);
  }

  return extCouponCode === couponCode;
}

/** Used to detect Fall25 on the cart (e.g. budget-step omission after select-plan or dialog claim). */
export function isFall25PromoOnSession(
  session: { promoCode?: { code?: string } | null } | null | undefined
): boolean {
  const code = session?.promoCode?.code;
  if (typeof code !== "string") {
    return false;
  }
  return code.toLowerCase() === FALL25_COUPON_CODE.toLowerCase();
}

export function getOfferHeadingCssOverride(
  showOfferHeading: boolean,
  headingClassName: string
): string {
  if (showOfferHeading) {
    return `
    .${headingClassName} {
      padding: 0 1rem;
      margin-top: 0;
    }
  `;
  }

  return `
    .${headingClassName} {
      padding: 0 1rem;
    }
  `;
}
